import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Stack, ListChecks, Plus, ChartBar, TreeStructure } from "@phosphor-icons/react";
import ProjectDeliverablesTree from "@/components/app/ProjectDeliverablesTree";
import ProjectDeliverablesGantt from "@/components/app/ProjectDeliverablesGantt";
import ProjectTaskBreakdown from "@/components/app/ProjectTaskBreakdown";

/**
 * ProjectDeliverablesSheet — focused right-side drawer that surfaces
 * Deliverables as the primary operational driver of a project (replacing
 * the legacy Lifecycle modal removed in v1.72).
 *
 * The sheet contains:
 *   1. Header with progress + counts
 *   2. The existing sub-breakdown tree (drag-reorder + per-deliverable
 *      audit timeline + push-to-tasks)
 *   3. The Task Breakdown panel below — tasks auto-synced from deliverables,
 *      grouped by project + deliverable, assigned only to real HR staff.
 *
 * Props:
 *   project    — full project record
 *   canEdit    — boolean
 *   onOpenEdit — () => void — caller wires this to open the full project
 *                Edit dialog (where new deliverables are added via
 *                DeliverableEditor)
 *   onReload   — refresh callback after backend mutations
 *   trigger    — optional custom trigger element
 */
export default function ProjectDeliverablesSheet({ project, canEdit, onOpenEdit, onReload, trigger }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("tree");  // tree | gantt
  const deliverables = project?.deliverables || [];
  const total = deliverables.length;
  const completed = deliverables.filter((d) => d.status === "completed" || d.status === "approved").length;
  const inProg = deliverables.filter((d) => d.status === "in_progress" || d.status === "review").length;
  const milestones = deliverables.filter((d) => d.milestone).length;
  const avgProgress = total > 0
    ? Math.round(deliverables.reduce((acc, d) => acc + (Number(d.completion_pct) || 0), 0) / total)
    : 0;

  const defaultTrigger = (
    <Button variant="default" size="sm" data-testid={`deliverables-trigger-${project.id}`}
            className="bg-[#1E5CBA] hover:bg-[#164a96] text-white">
      <Stack size={12} className="mr-1.5" weight="bold" /> Deliverables
      <span className="ml-1.5 inline-flex h-4 min-w-[1.1rem] items-center justify-center rounded bg-white/20 px-1 text-[10px] font-bold">
        {total}
      </span>
    </Button>
  );

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger || defaultTrigger}</SheetTrigger>
      <SheetContent side="right" className="!max-w-3xl w-full overflow-y-auto" data-testid={`deliverables-sheet-${project.id}`}>
        <SheetHeader className="pb-2">
          <SheetTitle className="font-display flex items-center gap-2">
            <ListChecks size={18} weight="bold" className="text-[#1E5CBA]" />
            Deliverables · {project.name}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {project.project_code ? `${project.project_code} · ` : ""}Main operational driver for this project.
            Edits sync to the Task Breakdown automatically.
          </SheetDescription>
        </SheetHeader>

        {/* Progress summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 mb-4">
          <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2">
            <div className="font-mono-alt text-[10px] uppercase tracking-wider text-slate-500">Total</div>
            <div className="mt-0.5 font-display text-base font-bold text-slate-900 tabular-nums">{total}</div>
          </div>
          <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2">
            <div className="font-mono-alt text-[10px] uppercase tracking-wider text-emerald-700">Done</div>
            <div className="mt-0.5 font-display text-base font-bold text-emerald-700 tabular-nums">{completed}</div>
          </div>
          <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2">
            <div className="font-mono-alt text-[10px] uppercase tracking-wider text-blue-700">In progress</div>
            <div className="mt-0.5 font-display text-base font-bold text-blue-700 tabular-nums">{inProg}</div>
          </div>
          <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2">
            <div className="font-mono-alt text-[10px] uppercase tracking-wider text-amber-700">Milestones</div>
            <div className="mt-0.5 font-display text-base font-bold text-amber-700 tabular-nums">{milestones}</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-3">
          <div className="flex justify-between font-mono-alt text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            <span>Overall progress</span>
            <span className="tabular-nums">{avgProgress}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden" data-testid={`deliverables-progress-${project.id}`}>
            <div className="h-full bg-gradient-to-r from-[#1E5CBA] to-emerald-500 transition-all"
                 style={{ width: `${avgProgress}%` }} />
          </div>
        </div>

        {/* Add / Edit shortcut button + view toggle */}
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          {canEdit && onOpenEdit && (
            <Button variant="outline" size="sm" onClick={() => { setOpen(false); onOpenEdit(); }}
                    data-testid={`deliverables-add-${project.id}`}>
              <Plus size={12} className="mr-1" /> Add / edit deliverables
            </Button>
          )}
          {/* Tree ↔ Gantt toggle */}
          <div className="ml-auto inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid={`deliverables-view-toggle-${project.id}`}>
            <button
              type="button"
              onClick={() => setView("tree")}
              className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-[11px] font-medium transition ${
                view === "tree" ? "bg-[#1E5CBA] text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
              data-testid={`deliverables-view-tree-${project.id}`}
            >
              <TreeStructure size={11} weight={view === "tree" ? "bold" : "regular"} /> Tree
            </button>
            <button
              type="button"
              onClick={() => setView("gantt")}
              className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-[11px] font-medium transition ${
                view === "gantt" ? "bg-[#1E5CBA] text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
              data-testid={`deliverables-view-gantt-${project.id}`}
            >
              <ChartBar size={11} weight={view === "gantt" ? "bold" : "regular"} /> Gantt
            </button>
          </div>
        </div>
        {canEdit && onOpenEdit && (
          <p className="text-[10px] text-slate-500 mb-3 -mt-2">
            Opens the project Edit dialog — use the Deliverables editor at the bottom to add or edit items, then save.
          </p>
        )}

        {/* Active view */}
        {view === "tree" ? (
          <ProjectDeliverablesTree
            project={project}
            canEdit={canEdit}
            onRowClick={() => { /* inline click is handled in tree */ }}
            onReload={onReload}
          />
        ) : (
          <ProjectDeliverablesGantt project={project} />
        )}

        {/* Task Breakdown — auto-synced tasks grouped by deliverable */}
        <ProjectTaskBreakdown projectId={project.id} />
      </SheetContent>
    </Sheet>
  );
}
